import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';

// ─── DESIGN SYSTEM ──────────────────────────────────────────
const colors = {
  primary: '#14562e',
  primaryLight: '#e6f0e9',
  primaryBorder: 'rgba(20,86,46,.2)',
  amber: '#e07c00',
  amberLight: '#fff7ed',
  red: '#d93025',
  redLight: '#fef2f2',
  blue: '#1a73e8',
  blueLight: '#eff6ff',
  text: '#1a1a1a',
  textSec: '#6b7280',
  textTer: '#9ca3af',
  border: '#e5e7eb',
  bg: '#f9fafb',
  card: '#ffffff',
  sidebar: '#ffffff',
};

const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: colors.bg, fontFamily: "'DM Sans', sans-serif" },
  sidebar: { width: '240px', background: colors.sidebar, borderRight: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', flexShrink: 0, position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 10 },
  main: { marginLeft: '240px', flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  header: { background: colors.card, borderBottom: `1px solid ${colors.border}`, padding: '0 28px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 5 },
  content: { padding: '28px', flex: 1 },
};

// ─── COMPONENTS ─────────────────────────────────────────────

function Logo() {
  return (
    <div style={{ padding: '20px 20px 16px', borderBottom: `1px solid ${colors.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>🔧</div>
        <div>
          <div style={{ fontWeight: 800, fontSize: '16px', color: colors.primary, letterSpacing: '-0.5px' }}>NICO</div>
          <div style={{ fontSize: '10px', color: colors.textTer, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Admin</div>
        </div>
      </div>
    </div>
  );
}

function NavItem({ icon, label, active, onClick, badge }) {
  return (
    <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 16px', margin: '2px 8px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: active ? colors.primaryLight : 'transparent', color: active ? colors.primary : colors.textSec, fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: active ? 600 : 500, width: 'calc(100% - 16px)', textAlign: 'left', transition: 'all 0.15s' }}>
      <span style={{ fontSize: '16px', width: '20px', textAlign: 'center' }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {badge > 0 && <span style={{ background: colors.red, color: 'white', borderRadius: '10px', padding: '1px 6px', fontSize: '10px', fontWeight: 700 }}>{badge}</span>}
    </button>
  );
}

function StatCard({ label, value, sub, icon, color = colors.primary, bg }) {
  return (
    <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '14px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: '12px', fontWeight: 600, color: colors.textTer, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>{label}</div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: colors.text, letterSpacing: '-1px' }}>{value}</div>
          {sub && <div style={{ fontSize: '12px', color: colors.textSec, marginTop: '4px' }}>{sub}</div>}
        </div>
        <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: bg || colors.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>{icon}</div>
      </div>
    </div>
  );
}

function Badge({ label, type = 'gray' }) {
  const map = {
    green: { bg: colors.primaryLight, color: colors.primary, border: colors.primaryBorder },
    amber: { bg: colors.amberLight, color: colors.amber, border: '#fed7aa' },
    red: { bg: colors.redLight, color: colors.red, border: '#fecaca' },
    blue: { bg: colors.blueLight, color: colors.blue, border: '#bfdbfe' },
    gray: { bg: '#f3f4f6', color: colors.textSec, border: colors.border },
  };
  const s = map[type] || map.gray;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      {label}
    </span>
  );
}

function Table({ columns, rows, emptyMessage = 'Nenhum dado encontrado' }) {
  return (
    <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '14px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ background: '#f9fafb', borderBottom: `1px solid ${colors.border}` }}>
            {columns.map((col, i) => (
              <th key={i} style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, color: colors.textSec, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={{ padding: '40px', textAlign: 'center', color: colors.textTer }}>
                {emptyMessage}
              </td>
            </tr>
          ) : rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: i < rows.length - 1 ? `1px solid ${colors.border}` : 'none', transition: 'background 0.1s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              {row.map((cell, j) => (
                <td key={j} style={{ padding: '12px 16px', color: colors.text, verticalAlign: 'middle' }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SearchBar({ value, onChange, placeholder }) {
  return (
    <div style={{ position: 'relative', width: '280px' }}>
      <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: colors.textTer, fontSize: '14px' }}>🔍</span>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder || 'Buscar...'} style={{ width: '100%', padding: '8px 12px 8px 34px', border: `1px solid ${colors.border}`, borderRadius: '8px', fontSize: '13px', fontFamily: "'DM Sans', sans-serif", color: colors.text, background: colors.card, outline: 'none' }} />
    </div>
  );
}

function SectionHeader({ title, subtitle, action }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
      <div>
        <h2 style={{ fontSize: '18px', fontWeight: 700, color: colors.text, margin: 0 }}>{title}</h2>
        {subtitle && <p style={{ fontSize: '13px', color: colors.textSec, margin: '2px 0 0' }}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function Spinner() {
  return <div style={{ width: '20px', height: '20px', border: '2px solid #e5e7eb', borderTopColor: '#14562e', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }}/>;
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
  } catch { return '—'; }
}

// ─── ADMIN LOGIN ────────────────────────────────────────────

function AdminLogin({ onSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await adminAPI.login(email, password);
      sessionStorage.setItem('nico_admin_token', r.token);
      onSuccess();
    } catch (err) {
      setError(err.message || 'Credenciais inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: colors.bg, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', padding: '40px', width: '100%', maxWidth: '400px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', margin: '0 auto 12px' }}>🔧</div>
          <h1 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: '24px', color: colors.primary, margin: '0 0 4px' }}>NICO Admin</h1>
          <p style={{ fontSize: '13px', color: colors.textSec, margin: 0 }}>Acesso restrito ao backoffice</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: colors.textSec, marginBottom: '6px', display: 'block' }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="admin@usenico.com.br"
              style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: `1.5px solid ${colors.border}`, fontSize: '14px', fontFamily: "'DM Sans', sans-serif", outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: colors.textSec, marginBottom: '6px', display: 'block' }}>Senha</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              onKeyDown={e => { if (e.key === 'Enter') handleSubmit(e); }}
              style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: `1.5px solid ${colors.border}`, fontSize: '14px', fontFamily: "'DM Sans', sans-serif", outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          {error && (
            <div style={{ background: colors.redLight, border: `1px solid #fecaca`, borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: colors.red, fontWeight: 500 }}>
              {error}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={loading || !email || !password}
            style={{ width: '100%', padding: '12px', borderRadius: '10px', border: 'none', background: colors.primary, color: '#fff', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}
          >
            {loading ? <Spinner /> : 'Entrar no Admin'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── VIEWS ──────────────────────────────────────────────────

function OverviewView({ stats }) {
  if (!stats) return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Spinner /></div>;

  const totalUsers = stats.users?.reduce((s, u) => s + (u.total || 0), 0) || 0;
  const owners = stats.users?.find(u => u.role === 'owner')?.total || 0;
  const pros = stats.users?.find(u => u.role === 'professional')?.total || 0;
  const totalServices = stats.services?.reduce((s, v) => s + (v.total || 0), 0) || 0;
  const validated = stats.services?.find(s => s.validation_status === 'validated')?.total || 0;

  return (
    <div>
      <SectionHeader title="Visão Geral" subtitle="Resumo do ecossistema NICO em tempo real"/>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '28px' }}>
        <StatCard label="Usuários totais" value={totalUsers} sub={`${owners} donos · ${pros} profissionais`} icon="👥" />
        <StatCard label="Profissionais" value={pros} sub={`${stats.professionals?.verified || 0} verificados`} icon="🔧" color={colors.amber} bg={colors.amberLight} />
        <StatCard label="Serviços registrados" value={totalServices} sub={`${validated} validados`} icon="📋" color={colors.blue} bg={colors.blueLight} />
        <StatCard label="Plano Pro" value={stats.professionals?.pro_plan || 0} sub="profissionais pagantes" icon="⭐" color={colors.amber} bg={colors.amberLight} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '14px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px' }}>Status dos Serviços</div>
          {stats.services?.map((s, i) => {
            const total = totalServices || 1;
            const pct = Math.round((s.total / total) * 100);
            const statusMap = { validated: { label: 'Validados', color: colors.primary }, pending: { label: 'Pendentes', color: colors.amber }, self_registered: { label: 'Auto-registrados', color: colors.blue }, rejected: { label: 'Rejeitados', color: colors.red } };
            const info = statusMap[s.validation_status] || { label: s.validation_status, color: colors.textSec };
            return (
              <div key={i} style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                  <span style={{ fontSize: '13px', color: colors.text, fontWeight: 500 }}>{info.label}</span>
                  <span style={{ fontSize: '13px', color: colors.textSec }}>{s.total} ({pct}%)</span>
                </div>
                <div style={{ height: '6px', background: '#f3f4f6', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: info.color, borderRadius: '3px', transition: 'width 0.5s ease' }}/>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '14px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px' }}>Profissionais</div>
          {[
            { label: 'Total cadastrados', value: stats.professionals?.total || 0, icon: '🔧' },
            { label: 'Verificados pelo NICO', value: stats.professionals?.verified || 0, icon: '✓', color: colors.primary },
            { label: 'Plano Pro ativo', value: stats.professionals?.pro_plan || 0, icon: '⭐', color: colors.amber },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < 2 ? `1px solid ${colors.border}` : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{item.icon}</span>
                <span style={{ fontSize: '13px', color: colors.text }}>{item.label}</span>
              </div>
              <span style={{ fontSize: '18px', fontWeight: 700, color: item.color || colors.text }}>{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Interesses Marketplace */}
      <InterestsCard />
      <ContactsCard />
    </div>
  );
}

function InterestsCard() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [tab, setTab] = useState('new'); // 'new' | 'read'
  const [marking, setMarking] = useState(null);

  const loadData = useCallback(() => {
    const { interestsAPI } = require('../services/api');
    interestsAPI.stats().then(setData).catch(() => setData(null));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (!data || data.total === 0) return null;

  const labels = {
    marketplace_assistencia24h: { name: 'Assistência 24h', emoji: '🆘' },
    marketplace_seguro: { name: 'Seguro Auto', emoji: '🛡️' },
    marketplace_abastecer: { name: 'Abastecer', emoji: '⛽' },
    marketplace_pedagio: { name: 'Pedágio', emoji: '🛣️' },
    marketplace_baterias: { name: 'Baterias', emoji: '🔋' },
    marketplace_emprestimo: { name: 'Empréstimo', emoji: '💰' },
  };

  const fmtDateTime = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
  };

  const handleToggleRead = async (interest) => {
    const { interestsAPI } = require('../services/api');
    setMarking(interest.id);
    try {
      if (interest.read_at) {
        await interestsAPI.markUnread(interest.id);
      } else {
        await interestsAPI.markRead(interest.id);
      }
      loadData();
    } catch { }
    finally { setMarking(null); }
  };

  // Separar detalhes por tab
  const allDetails = data.details || [];
  const newDetails = allDetails.filter(d => !d.read_at);
  const readDetails = allDetails.filter(d => d.read_at);
  const currentDetails = tab === 'new' ? newDetails : readDetails;

  // Agrupar por service_key
  const groupByService = (items) => {
    const groups = {};
    items.forEach(d => {
      if (!groups[d.service_key]) groups[d.service_key] = [];
      groups[d.service_key].push(d);
    });
    return groups;
  };
  const grouped = groupByService(currentDetails);
  const serviceKeys = Object.keys(grouped);

  return (
    <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '14px', padding: '20px', marginTop: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
      {/* Header + tabs */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.5px' }}>🔔 Interesses no Marketplace</div>
        <span style={{ fontSize: '13px', fontWeight: 700, color: colors.primary }}>{data.total} total</span>
      </div>

      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
        <button
          onClick={() => { setTab('new'); setExpanded(null); }}
          style={{
            padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
            cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
            border: tab === 'new' ? `1px solid ${colors.primary}` : `1px solid ${colors.border}`,
            background: tab === 'new' ? colors.primaryLight : 'transparent',
            color: tab === 'new' ? colors.primary : colors.textSec,
          }}
        >
          Novos {newDetails.length > 0 ? <span style={{ background: colors.amber, color: '#fff', fontSize: '10px', fontWeight: 700, padding: '1px 5px', borderRadius: '999px', marginLeft: '4px' }}>{newDetails.length}</span> : null}
        </button>
        <button
          onClick={() => { setTab('read'); setExpanded(null); }}
          style={{
            padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
            cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
            border: tab === 'read' ? `1px solid ${colors.primary}` : `1px solid ${colors.border}`,
            background: tab === 'read' ? colors.primaryLight : 'transparent',
            color: tab === 'read' ? colors.primary : colors.textSec,
          }}
        >
          Lidos {readDetails.length > 0 ? <span style={{ background: '#d1d5db', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '1px 5px', borderRadius: '999px', marginLeft: '4px' }}>{readDetails.length}</span> : null}
        </button>
      </div>

      {/* Barras por serviço */}
      {serviceKeys.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px', color: colors.textTer, fontSize: '13px' }}>
          {tab === 'new' ? 'Nenhum lead novo' : 'Nenhum lead lido ainda'}
        </div>
      ) : serviceKeys.map((key, i) => {
        const info = labels[key] || { name: key, emoji: '📦' };
        const people = grouped[key];
        const isOpen = expanded === key;

        return (
          <div key={i} style={{ marginBottom: '4px' }}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', cursor: 'pointer', borderRadius: '8px', transition: 'background 0.1s' }}
              onClick={() => setExpanded(isOpen ? null : key)}
              onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ fontSize: '16px', width: '24px', textAlign: 'center' }}>{info.emoji}</span>
              <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: colors.text, fontWeight: 500 }}>{info.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '13px', color: colors.textSec, fontWeight: 600 }}>{people.length}</span>
                  <span style={{ fontSize: '10px', color: colors.textTer, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none' }}>▼</span>
                </div>
              </div>
            </div>

            {isOpen && (
              <div style={{ marginLeft: '34px', marginBottom: '12px', borderLeft: `2px solid ${colors.border}`, paddingLeft: '14px' }}>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {people.map((p, j) => (
                    <div key={j} style={{
                      display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0',
                      borderBottom: j < people.length - 1 ? `1px solid ${colors.border}` : 'none',
                      opacity: marking === p.id ? 0.4 : 1, transition: 'opacity 0.2s',
                    }}>
                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: colors.text }}>{p.name || '—'}</div>
                        <div style={{ fontSize: '12px', color: colors.textSec }}>{p.email || '—'}</div>
                        {p.phone && <div style={{ fontSize: '12px', color: colors.textTer }}>📞 {p.phone}</div>}
                      </div>
                      {/* Data */}
                      <span style={{ fontSize: '11px', color: colors.textTer, whiteSpace: 'nowrap' }}>{fmtDateTime(p.created_at)}</span>
                      {/* Botão marcar */}
                      <button
                        onClick={e => { e.stopPropagation(); handleToggleRead(p); }}
                        disabled={marking === p.id}
                        title={p.read_at ? 'Marcar como novo' : 'Marcar como lido'}
                        style={{
                          padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                          cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap',
                          transition: 'all 0.15s', flexShrink: 0,
                          border: p.read_at ? `1px solid ${colors.primaryBorder}` : `1px solid ${colors.border}`,
                          background: p.read_at ? colors.primaryLight : '#f9fafb',
                          color: p.read_at ? colors.primary : colors.textSec,
                        }}
                      >
                        {p.read_at ? '↩ Novo' : '✓ Lido'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ContactsCard() {
  const [data, setData] = useState(null);
  const [marking, setMarking] = useState(null);

  const load = useCallback(() => {
    const { contactsAPI } = require('../services/api');
    contactsAPI.list().then(setData).catch(() => setData(null));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data || !data.contacts?.length) return null;

  const fmtDt = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')} ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
  };

  const typeLabels = { oficina: '🔧 Oficina', frota: '🚛 Frota', parceiro: '🤝 Parceiro', outro: '📦 Outro' };
  const unread = data.contacts.filter(c => !c.read_at);
  const read = data.contacts.filter(c => c.read_at);

  const handleRead = async (id) => {
    const { contactsAPI } = require('../services/api');
    setMarking(id);
    try { await contactsAPI.markRead(id); load(); }
    catch {}
    finally { setMarking(null); }
  };

  return (
    <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '14px', padding: '20px', marginTop: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.5px' }}>📋 Contatos Recebidos</div>
        {unread.length > 0 && <span style={{ background: colors.red, color: '#fff', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px' }}>{unread.length} novo{unread.length > 1 ? 's' : ''}</span>}
      </div>

      {unread.map((c, i) => (
        <div key={c.id} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '12px 0', borderBottom: i < unread.length - 1 || read.length > 0 ? `1px solid ${colors.border}` : 'none', opacity: marking === c.id ? 0.4 : 1 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{ fontWeight: 600, fontSize: '14px', color: colors.text }}>{c.name}</span>
              <Badge label={typeLabels[c.type] || c.type} type="amber"/>
            </div>
            <div style={{ fontSize: '13px', color: colors.textSec }}>{c.email}{c.phone ? ` · ${c.phone}` : ''}</div>
            {c.company && <div style={{ fontSize: '12px', color: colors.textTer }}>{c.company}</div>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            <span style={{ fontSize: '11px', color: colors.textTer }}>{fmtDt(c.created_at)}</span>
            <button onClick={() => handleRead(c.id)} disabled={marking === c.id} style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', border: `1px solid ${colors.border}`, background: '#f9fafb', color: colors.textSec, fontFamily: "'DM Sans',sans-serif" }}>✓ Lido</button>
          </div>
        </div>
      ))}

      {read.length > 0 && (
        <details style={{ marginTop: '8px' }}>
          <summary style={{ fontSize: '12px', color: colors.textTer, cursor: 'pointer', padding: '8px 0', fontWeight: 600 }}>
            {read.length} contato{read.length > 1 ? 's' : ''} lido{read.length > 1 ? 's' : ''}
          </summary>
          {read.map((c, i) => (
            <div key={c.id} style={{ display: 'flex', gap: '12px', padding: '8px 0', borderBottom: i < read.length - 1 ? `1px solid ${colors.border}` : 'none', opacity: 0.6 }}>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 500, fontSize: '13px', color: colors.textSec }}>{c.name}</span>
                <span style={{ fontSize: '12px', color: colors.textTer, marginLeft: '8px' }}>{c.email}</span>
              </div>
              <span style={{ fontSize: '11px', color: colors.textTer }}>{fmtDt(c.created_at)}</span>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

function UsersView() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filter !== 'all') params.role = filter;
      const r = await adminAPI.users(params);
      setUsers(r.users || []);
    } catch { toast.error('Erro ao carregar usuários'); }
    finally { setLoading(false); }
  }, [search, filter]);

  useEffect(() => { load(); }, [load]);

  const handleStatus = async (id, status) => {
    try {
      await adminAPI.updateUserStatus(id, status);
      toast.success(status === 'banned' ? 'Usuário banido' : 'Usuário reativado');
      load();
    } catch { toast.error('Erro ao atualizar'); }
  };

  const rows = users.map(u => [
    <div>
      <div style={{ fontWeight: 600, fontSize: '13px', color: colors.text }}>{u.name}</div>
      <div style={{ fontSize: '12px', color: colors.textSec }}>{u.email}</div>
    </div>,
    <Badge label={u.role === 'owner' ? '🚗 Dono' : u.role === 'professional' ? '🔧 Pro' : '⚙️ Admin'} type={u.role === 'professional' ? 'green' : u.role === 'admin' ? 'blue' : 'gray'}/>,
    <Badge label={u.status === 'active' ? 'Ativo' : u.status === 'banned' ? 'Banido' : u.status || 'Ativo'} type={!u.status || u.status === 'active' ? 'green' : 'red'}/>,
    // [NEW 13/set/2026] Nº de veículos ativos + modelos, vindos de admin.js (GET /users,
    // agora com LEFT JOIN + agregação em vehicles). vehicle_count vem sempre (0 se não
    // tiver nenhum); vehicle_models só existe quando vehicle_count > 0.
    <div title={u.vehicle_models || ''}>
      <div style={{ fontWeight: 600, fontSize: '12.5px', color: u.vehicle_count > 0 ? colors.text : colors.textTer }}>
        {u.vehicle_count > 0 ? `🚗 ${u.vehicle_count}` : '—'}
      </div>
      {u.vehicle_count > 0 && (
        <div style={{ fontSize: '11px', color: colors.textSec, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {u.vehicle_models}
        </div>
      )}
    </div>,
    <span style={{ fontSize: '12px', color: colors.textSec }}>{fmtDate(u.created_at)}</span>,
    <div style={{ display: 'flex', gap: '6px' }}>
      {(!u.status || u.status === 'active') ? (
        <button onClick={() => handleStatus(u.id, 'banned')} style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #fecaca', background: colors.redLight, color: colors.red, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
          Banir
        </button>
      ) : (
        <button onClick={() => handleStatus(u.id, 'active')} style={{ padding: '4px 10px', borderRadius: '6px', border: `1px solid ${colors.primaryBorder}`, background: colors.primaryLight, color: colors.primary, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
          Reativar
        </button>
      )}
    </div>
  ]);

  return (
    <div>
      <SectionHeader title="Usuários" subtitle={`${users.length} usuário${users.length !== 1 ? 's' : ''} encontrado${users.length !== 1 ? 's' : ''}`}/>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Buscar por nome ou email..."/>
        <div style={{ display: 'flex', gap: '6px' }}>
          {[['all', 'Todos'], ['owner', 'Donos'], ['professional', 'Profissionais']].map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)} style={{ padding: '7px 14px', borderRadius: '8px', border: `1px solid ${filter === val ? colors.primary : colors.border}`, background: filter === val ? colors.primaryLight : colors.card, color: filter === val ? colors.primary : colors.textSec, fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', fontFamily: "'DM Sans', sans-serif" }}>
              {label}
            </button>
          ))}
        </div>
        {loading && <Spinner />}
      </div>
      <Table columns={['Usuário', 'Tipo', 'Status', 'Veículos', 'Cadastro', 'Ações']} rows={rows} emptyMessage="Nenhum usuário encontrado"/>
    </div>
  );
}

function ProfessionalsView() {
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const r = await adminAPI.professionals(params);
      setProfessionals(r.professionals || []);
    } catch { toast.error('Erro ao carregar profissionais'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleVerify = async (proId) => {
    try {
      await adminAPI.verifyProfessional(proId);
      toast.success('Profissional verificado ✓');
      load();
    } catch { toast.error('Erro ao verificar'); }
  };

  const rows = professionals.map(p => [
    <div>
      <div style={{ fontWeight: 600, fontSize: '13px', color: colors.text }}>{p.business_name || p.user_name}</div>
      <div style={{ fontSize: '12px', color: colors.textSec }}>{p.email}</div>
      {p.address_city && <div style={{ fontSize: '11px', color: colors.textTer }}>📍 {p.address_city}{p.address_state ? `, ${p.address_state}` : ''}</div>}
    </div>,
    <div style={{ display: 'flex', gap: '4px', flexDirection: 'column', alignItems: 'flex-start' }}>
      <Badge label={p.user_status === 'active' ? 'Ativo' : 'Inativo'} type={p.user_status === 'active' ? 'green' : 'red'}/>
      {p.is_verified && <Badge label="✓ Verificado" type="blue"/>}
    </div>,
    <div>
      <div style={{ fontSize: '12px', color: colors.textSec }}>{p.specialty || '—'}</div>
      <div style={{ fontSize: '11px', color: colors.textTer }}>{p.whatsapp || ''}</div>
    </div>,
    <span style={{ fontSize: '12px', color: colors.textSec }}>{fmtDate(p.created_at)}</span>,
    <div style={{ display: 'flex', gap: '6px' }}>
      {!p.is_verified && (
        <button onClick={() => handleVerify(p.id)} style={{ padding: '4px 10px', borderRadius: '6px', border: `1px solid ${colors.primaryBorder}`, background: colors.primaryLight, color: colors.primary, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
          ✓ Verificar
        </button>
      )}
      {p.is_verified && <span style={{ fontSize: '12px', color: colors.primary, fontWeight: 600 }}>✓ Verificado</span>}
    </div>
  ]);

  return (
    <div>
      <SectionHeader title="Profissionais" subtitle="Gerencie e verifique perfis de profissionais"/>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Buscar profissional..."/>
        {loading && <Spinner />}
      </div>
      <Table columns={['Profissional', 'Status', 'Especialidade', 'Cadastro', 'Ações']} rows={rows} emptyMessage="Nenhum profissional encontrado"/>
    </div>
  );
}

function VehiclesView() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!search.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const r = await adminAPI.searchVehicles(search);
      setVehicles(r.vehicles || []);
    } catch { toast.error('Erro ao buscar veículos'); }
    finally { setLoading(false); }
  };

  const typeLabels = { car: '🚗 Carro', motorcycle: '🏍️ Moto', truck: '🛻 Caminhonete', boat: '⛵ Barco', tractor: '🚜 Trator', other: '🔧 Outro' };

  const rows = vehicles.map(v => [
    <div style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 600, letterSpacing: '1px', color: colors.primary }}>{v.plate || '—'}</div>,
    <div>
      <div style={{ fontWeight: 600, fontSize: '13px' }}>{v.brand} {v.model}</div>
      <div style={{ fontSize: '12px', color: colors.textSec }}>{v.year} · {v.nickname || '—'}</div>
    </div>,
    <span style={{ fontSize: '13px' }}>{typeLabels[v.type] || v.type}</span>,
    <span style={{ fontSize: '13px', fontWeight: 600 }}>{(v.current_km || 0).toLocaleString('pt-BR')} km</span>,
    <div>
      <div style={{ fontSize: '13px', fontWeight: 500 }}>{v.owner_name || '—'}</div>
      <div style={{ fontSize: '12px', color: colors.textSec }}>{v.owner_email || ''}</div>
    </div>,
    <Badge label={v.is_active ? 'Ativo' : 'Inativo'} type={v.is_active ? 'green' : 'gray'}/>,
  ]);

  return (
    <div>
      <SectionHeader title="Veículos" subtitle="Busque veículos por placa, marca ou proprietário"/>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Buscar por placa, marca, modelo..."/>
        <button onClick={handleSearch} disabled={loading} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: colors.primary, color: 'white', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", display: 'flex', alignItems: 'center', gap: '6px' }}>
          {loading ? <Spinner /> : '🔍'} Buscar
        </button>
      </div>
      {!searched ? (
        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '14px', padding: '60px', textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>🚗</div>
          <div style={{ fontWeight: 600, color: colors.text, marginBottom: '6px' }}>Busque um veículo</div>
          <div style={{ fontSize: '13px', color: colors.textSec }}>Digite a placa, marca ou modelo para encontrar</div>
        </div>
      ) : (
        <Table columns={['Placa', 'Veículo', 'Tipo', 'KM', 'Proprietário', 'Status']} rows={rows} emptyMessage="Nenhum veículo encontrado"/>
      )}
    </div>
  );
}

function ServicesView() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    adminAPI.stats().then(r => setStats(r.stats)).catch(() => {});
  }, []);

  const totalServices = stats?.services?.reduce((s, v) => s + (v.total || 0), 0) || 0;

  return (
    <div>
      <SectionHeader title="Serviços" subtitle="Visão geral dos serviços registrados na plataforma"/>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'Total', key: null, color: colors.primary, bg: colors.primaryLight, icon: '📋' },
          { label: 'Validados', key: 'validated', color: colors.primary, bg: colors.primaryLight, icon: '✓' },
          { label: 'Pendentes', key: 'pending', color: colors.amber, bg: colors.amberLight, icon: '⏳' },
          { label: 'Auto-registrados', key: 'self_registered', color: colors.blue, bg: colors.blueLight, icon: '📝' },
        ].map((item, i) => {
          const value = item.key ? stats?.services?.find(s => s.validation_status === item.key)?.total || 0 : totalServices;
          return <StatCard key={i} label={item.label} value={value} icon={item.icon} color={item.color} bg={item.bg}/>;
        })}
      </div>

      <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '14px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px' }}>Distribuição por status</div>
        {stats?.services?.map((s, i) => {
          const total = totalServices || 1;
          const pct = Math.round((s.total / total) * 100);
          const statusMap = { validated: { label: 'Validados', color: colors.primary }, pending: { label: 'Pendentes', color: colors.amber }, self_registered: { label: 'Auto-registrados', color: colors.blue }, rejected: { label: 'Rejeitados', color: colors.red } };
          const info = statusMap[s.validation_status] || { label: s.validation_status, color: colors.textSec };
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
              <div style={{ width: '120px', fontSize: '13px', color: colors.text }}>{info.label}</div>
              <div style={{ flex: 1, height: '8px', background: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: info.color, borderRadius: '4px' }}/>
              </div>
              <div style={{ width: '60px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: colors.text }}>{s.total}</div>
              <div style={{ width: '40px', textAlign: 'right', fontSize: '12px', color: colors.textSec }}>{pct}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlateConflictsView() {
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open'); // 'open' | 'all'
  const [resolvingId, setResolvingId] = useState(null);
  const [resolutionText, setResolutionText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAPI.plateConflicts({ status: filter });
      setConflicts(r.conflicts || []);
    } catch { toast.error('Erro ao carregar conflitos de placa'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleResolve = async (id) => {
    try {
      await adminAPI.resolvePlateConflict(id, resolutionText);
      toast.success('Conflito marcado como resolvido');
      setResolvingId(null);
      setResolutionText('');
      load();
    } catch { toast.error('Erro ao resolver conflito'); }
  };

  const rows = conflicts.map(c => [
    <div style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 600, letterSpacing: '1px', color: colors.primary }}>{c.plate}</div>,
    <div>
      <div style={{ fontSize: '13px', fontWeight: 600, color: colors.text }}>{c.existing_brand} {c.existing_model} · {c.existing_year}</div>
      <div style={{ fontSize: '12px', color: colors.textSec }}>{c.existing_owner_name}</div>
      <div style={{ fontSize: '11px', color: colors.textTer }}>{c.existing_owner_email}</div>
    </div>,
    <div>
      <div style={{ fontSize: '13px', fontWeight: 600, color: colors.text }}>{c.new_brand} {c.new_model} · {c.new_year}</div>
      <div style={{ fontSize: '12px', color: colors.textSec }}>{c.new_owner_name}</div>
      <div style={{ fontSize: '11px', color: colors.textTer }}>{c.new_owner_email}</div>
    </div>,
    <span style={{ fontSize: '12px', color: colors.textSec }}>{fmtDate(c.created_at)}</span>,
    c.resolved_at ? (
      <Badge label="Resolvido" type="green"/>
    ) : resolvingId === c.id ? (
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          value={resolutionText}
          onChange={e => setResolutionText(e.target.value)}
          placeholder="Nota (opcional)"
          style={{ padding: '6px 8px', borderRadius: '6px', border: `1px solid ${colors.border}`, fontSize: '12px', width: '140px', fontFamily: "'DM Sans', sans-serif" }}
        />
        <button onClick={() => handleResolve(c.id)} style={{ padding: '5px 10px', borderRadius: '6px', border: 'none', background: colors.primary, color: '#fff', fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
          Salvar
        </button>
        <button onClick={() => { setResolvingId(null); setResolutionText(''); }} style={{ padding: '5px 10px', borderRadius: '6px', border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textSec, fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
          Cancelar
        </button>
      </div>
    ) : (
      <button onClick={() => setResolvingId(c.id)} style={{ padding: '4px 10px', borderRadius: '6px', border: `1px solid ${colors.primaryBorder}`, background: colors.primaryLight, color: colors.primary, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
        Marcar resolvido
      </button>
    )
  ]);

  return (
    <div>
      <SectionHeader title="Conflitos de placa" subtitle={`${conflicts.length} conflito${conflicts.length !== 1 ? 's' : ''} ${filter === 'open' ? 'em aberto' : 'no total'} — cadastro novo bateu com uma placa já ativa em outra conta`}/>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', alignItems: 'center' }}>
        {[['open', 'Em aberto'], ['all', 'Todos']].map(([val, label]) => (
          <button key={val} onClick={() => setFilter(val)} style={{ padding: '7px 14px', borderRadius: '8px', border: `1px solid ${filter === val ? colors.primary : colors.border}`, background: filter === val ? colors.primaryLight : colors.card, color: filter === val ? colors.primary : colors.textSec, fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', fontFamily: "'DM Sans', sans-serif" }}>
            {label}
          </button>
        ))}
        {loading && <Spinner />}
      </div>
      <Table columns={['Placa', 'Cadastro existente', 'Cadastro novo', 'Detectado em', 'Ação']} rows={rows} emptyMessage="Nenhum conflito de placa encontrado"/>
    </div>
  );
}

function SubscriptionsView() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filter !== 'all') params.status = filter;
      const r = await adminAPI.subscriptions(params);
      setSubscriptions(r.subscriptions || []);
    } catch { toast.error('Erro ao carregar assinaturas'); }
    finally { setLoading(false); }
  }, [search, filter]);

  useEffect(() => { load(); }, [load]);

  const statusMap = {
    active: { label: 'Ativa', type: 'green' },
    trialing: { label: 'Em teste', type: 'blue' },
    past_due: { label: 'Pagamento pendente', type: 'amber' },
    pending: { label: 'Pendente', type: 'gray' },
    cancelled: { label: 'Cancelada', type: 'red' },
  };

  const handleCancel = async (sub) => {
    const label = `${sub.brand || ''} ${sub.model || ''}`.trim() || sub.plate || 'este veículo';
    if (!window.confirm(`Cancelar agora a assinatura de ${label} (${sub.owner_name || sub.owner_email})? O acesso ao histórico é bloqueado imediatamente — isso não pode ser desfeito por aqui.`)) {
      return;
    }
    setCancelling(sub.id);
    try {
      await adminAPI.cancelSubscription(sub.id);
      toast.success('Assinatura cancelada');
      load();
    } catch (err) {
      toast.error(err.message || 'Erro ao cancelar assinatura');
    } finally {
      setCancelling(null);
    }
  };

  const rows = subscriptions.map(s => [
    <div>
      <div style={{ fontWeight: 600, fontSize: '13px', color: colors.text }}>{s.brand} {s.model}</div>
      <div style={{ fontSize: '12px', color: colors.textSec }}>{s.plate || s.nickname || '—'}</div>
    </div>,
    <div>
      <div style={{ fontSize: '13px', fontWeight: 500, color: colors.text }}>{s.owner_name || '—'}</div>
      <div style={{ fontSize: '12px', color: colors.textSec }}>{s.owner_email || ''}</div>
    </div>,
    <Badge label={(statusMap[s.status] || { label: s.status }).label} type={(statusMap[s.status] || {}).type || 'gray'}/>,
    <span style={{ fontSize: '12px', color: colors.textSec }}>{fmtDate(s.current_period_end)}</span>,
    <span style={{ fontSize: '12px', color: colors.textSec }}>{fmtDate(s.created_at)}</span>,
    <div style={{ display: 'flex', gap: '6px' }}>
      {['active', 'past_due', 'trialing'].includes(s.status) ? (
        <button onClick={() => handleCancel(s)} disabled={cancelling === s.id} style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #fecaca', background: colors.redLight, color: colors.red, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", opacity: cancelling === s.id ? 0.6 : 1 }}>
          {cancelling === s.id ? 'Cancelando...' : 'Cancelar agora'}
        </button>
      ) : (
        <span style={{ fontSize: '12px', color: colors.textTer }}>—</span>
      )}
    </div>
  ]);

  return (
    <div>
      <SectionHeader title="Assinaturas" subtitle={`${subscriptions.length} assinatura${subscriptions.length !== 1 ? 's' : ''} de veículo adicional`}/>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Buscar por placa, dono ou email..."/>
        <div style={{ display: 'flex', gap: '6px' }}>
          {[['all', 'Todas'], ['active', 'Ativas'], ['past_due', 'Pendentes'], ['cancelled', 'Canceladas']].map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)} style={{ padding: '7px 14px', borderRadius: '8px', border: `1px solid ${filter === val ? colors.primary : colors.border}`, background: filter === val ? colors.primaryLight : colors.card, color: filter === val ? colors.primary : colors.textSec, fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', fontFamily: "'DM Sans', sans-serif" }}>
              {label}
            </button>
          ))}
        </div>
        {loading && <Spinner />}
      </div>
      <Table columns={['Veículo', 'Proprietário', 'Status', 'Período até', 'Criada em', 'Ações']} rows={rows} emptyMessage="Nenhuma assinatura encontrada"/>
    </div>
  );
}

function SettingsView() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  const loadFlags = useCallback(async () => {
    try {
      const r = await adminAPI.features();
      setFlags(r.flags || []);
    } catch { toast.error('Erro ao carregar feature flags'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadFlags(); }, [loadFlags]);

  const handleToggle = async (key, currentState) => {
    setToggling(key);
    try {
      await adminAPI.toggleFeature(key, !currentState);
      setFlags(prev => prev.map(f => f.key === key ? { ...f, enabled: !currentState } : f));
      toast.success(`${key} ${!currentState ? 'ativado' : 'desativado'}`);
    } catch { toast.error('Erro ao alterar feature'); }
    finally { setToggling(null); }
  };

  // Agrupa flags por categoria
  const marketplaceFlags = flags.filter(f => f.key.startsWith('marketplace_'));
  const coreFlags = flags.filter(f => !f.key.startsWith('marketplace_'));

  const renderFlagRow = (flag) => {
    const isOn = flag.enabled === true || flag.enabled === 1;
    const isLoading = toggling === flag.key;

    return (
      <div key={flag.key} style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 0', borderBottom: `1px solid ${colors.border}`,
        opacity: isLoading ? 0.5 : 1, transition: 'opacity 0.2s',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: 600, color: colors.text }}>{flag.label || flag.key}</span>
            {flag.roles && <span style={{ fontSize: '10px', background: colors.blueLight, color: colors.blue, padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>{flag.roles}</span>}
          </div>
          <div style={{ fontSize: '12px', color: colors.textTer, marginTop: '2px' }}>{flag.description || flag.key}</div>
        </div>
        <button
          onClick={() => handleToggle(flag.key, isOn)}
          disabled={isLoading}
          style={{
            width: '48px', height: '26px', borderRadius: '999px', border: 'none',
            background: isOn ? colors.primary : '#d1d5db', cursor: 'pointer',
            position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginLeft: '16px',
          }}
        >
          <span style={{
            width: '22px', height: '22px', borderRadius: '50%', background: '#fff',
            position: 'absolute', top: '2px', left: isOn ? '24px' : '2px',
            transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)',
          }} />
        </button>
      </div>
    );
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Spinner /></div>;

  return (
    <div>
      <SectionHeader title="Configurações" subtitle="Feature flags e configurações do sistema"/>

      {/* Marketplace Services */}
      {marketplaceFlags.length > 0 && (
        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '14px', padding: '24px', marginBottom: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <span style={{ fontSize: '20px' }}>🛒</span>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: colors.text }}>Marketplace de Serviços</div>
              <div style={{ fontSize: '12px', color: colors.textSec }}>Ative ou desative serviços no painel do usuário</div>
            </div>
          </div>
          {marketplaceFlags.map(renderFlagRow)}
        </div>
      )}

      {/* Core Features */}
      {coreFlags.length > 0 && (
        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '14px', padding: '24px', marginBottom: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <span style={{ fontSize: '20px' }}>⚙️</span>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: colors.text }}>Funcionalidades do App</div>
              <div style={{ fontSize: '12px', color: colors.textSec }}>Controle de features gerais da plataforma</div>
            </div>
          </div>
          {coreFlags.map(renderFlagRow)}
        </div>
      )}

      {/* System Info */}
      <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '14px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <span style={{ fontSize: '20px' }}>📡</span>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: colors.text }}>Sistema</div>
            <div style={{ fontSize: '12px', color: colors.textSec }}>Informações do ambiente</div>
          </div>
        </div>
        {[
          { label: 'Versão do app', value: '2.0.0', icon: '📱' },
          { label: 'Máx. veículos (gratuito)', value: '3', icon: '🚗' },
          { label: 'Banco de dados', value: 'Supabase PostgreSQL', icon: '🗄️' },
          { label: 'Backend', value: 'Render (Node.js)', icon: '⚙️' },
          { label: 'Frontend', value: 'Cloudflare Pages', icon: '🌐' },
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < 4 ? `1px solid ${colors.border}` : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '16px' }}>{item.icon}</span>
              <span style={{ fontSize: '13px', color: colors.text }}>{item.label}</span>
            </div>
            <span style={{ fontSize: '13px', color: colors.textSec }}>{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ─────────────────────────────────────────

function AlertTemplatesView() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = useCallback(async () => {
    try {
      const { alertTemplatesAPI } = require('../services/api');
      const r = await alertTemplatesAPI.list();
      setTemplates(r.templates || []);
    } catch { toast.error('Erro ao carregar templates'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (tmpl) => {
    const { alertTemplatesAPI } = require('../services/api');
    setToggling(tmpl.id);
    try {
      const newState = !(tmpl.is_active === true || tmpl.is_active === 1);
      await alertTemplatesAPI.update(tmpl.id, { is_active: newState });
      toast.success(`${tmpl.title} ${newState ? 'ativado' : 'desativado'}`);
      load();
    } catch { toast.error('Erro ao alterar'); }
    finally { setToggling(null); }
  };

  const handleEdit = (tmpl) => {
    setEditing(tmpl.id);
    setEditForm({
      title: tmpl.title,
      message: tmpl.message,
      trigger_km: tmpl.trigger_km || '',
      trigger_days: tmpl.trigger_days || '',
      // [NEW] Estado inicial do toggle de email — trata qualquer coisa que não seja
      // explicitamente 0/false como "ligado" (mesmo padrão defensivo usado no resto do
      // componente pra is_active/is_welcome, já que não temos certeza se o driver do
      // banco devolve 0/1 ou true/false pra essa coluna).
      send_email: !(tmpl.send_email === false || tmpl.send_email === 0),
    });
  };

  const handleSave = async (id) => {
    const { alertTemplatesAPI } = require('../services/api');
    try {
      await alertTemplatesAPI.update(id, {
        title: editForm.title,
        message: editForm.message,
        trigger_km: editForm.trigger_km ? parseInt(editForm.trigger_km) : null,
        trigger_days: editForm.trigger_days ? parseInt(editForm.trigger_days) : null,
        // [NEW] Liga/desliga o email de lembrete pra este template específico.
        send_email: !!editForm.send_email,
      });
      toast.success('Template salvo');
      setEditing(null);
      load();
    } catch { toast.error('Erro ao salvar'); }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Spinner /></div>;

  const welcome = templates.filter(t => t.is_welcome === true || t.is_welcome === 1);
  const maintenance = templates.filter(t => !(t.is_welcome === true || t.is_welcome === 1));

  const renderTemplate = (tmpl) => {
    const isOn = tmpl.is_active === true || tmpl.is_active === 1;
    const isEditing = editing === tmpl.id;
    // [NEW] Mesma leitura defensiva usada em handleEdit — decide o badge "Email"/"Email off".
    const emailOn = !(tmpl.send_email === false || tmpl.send_email === 0);

    return (
      <div key={tmpl.id} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', padding: '14px 0', borderBottom: `1px solid ${colors.border}`, opacity: toggling === tmpl.id ? 0.4 : 1 }}>
        <button onClick={() => handleToggle(tmpl)} disabled={toggling === tmpl.id} style={{ width: '44px', height: '24px', borderRadius: '999px', border: 'none', background: isOn ? colors.primary : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginTop: '2px' }}>
          <span style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px', left: isOn ? '22px' : '2px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
        </button>
        <div style={{ flex: 1 }}>
          {isEditing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <input value={editForm.title} onChange={e => setEditForm({...editForm, title: e.target.value})} placeholder="Título" style={{ padding: '8px 12px', borderRadius: '8px', border: `1px solid ${colors.border}`, fontSize: '14px', fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }} />
              <input value={editForm.message} onChange={e => setEditForm({...editForm, message: e.target.value})} placeholder="Mensagem" style={{ padding: '8px 12px', borderRadius: '8px', border: `1px solid ${colors.border}`, fontSize: '13px', fontFamily: "'DM Sans', sans-serif" }} />
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input value={editForm.trigger_km} onChange={e => setEditForm({...editForm, trigger_km: e.target.value})} placeholder="KM gatilho" type="number" style={{ padding: '8px 12px', borderRadius: '8px', border: `1px solid ${colors.border}`, fontSize: '13px', width: '120px', fontFamily: "'DM Sans', sans-serif" }} />
                <input value={editForm.trigger_days} onChange={e => setEditForm({...editForm, trigger_days: e.target.value})} placeholder="Dias gatilho" type="number" style={{ padding: '8px 12px', borderRadius: '8px', border: `1px solid ${colors.border}`, fontSize: '13px', width: '120px', fontFamily: "'DM Sans', sans-serif" }} />
                {/* [NEW] Liga/desliga o email de lembrete pra este template — é o
                    "parametrizado no backoffice" pedido junto da feature de email. */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: colors.textSec, cursor: 'pointer', padding: '8px 12px', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
                  <input type="checkbox" checked={!!editForm.send_email} onChange={e => setEditForm({...editForm, send_email: e.target.checked})} />
                  📧 Enviar email de lembrete
                </label>
                <button onClick={() => handleSave(tmpl.id)} style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', background: colors.primary, color: '#fff', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>Salvar</button>
                <button onClick={() => setEditing(null)} style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textSec, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>Cancelar</button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, fontSize: '14px', color: isOn ? colors.text : colors.textTer }}>{tmpl.title}</span>
                {(tmpl.is_welcome === true || tmpl.is_welcome === 1) && <Badge label="Boas-vindas" type="green" />}
                {tmpl.type === 'km' && <Badge label={`${(tmpl.trigger_km || 0).toLocaleString('pt-BR')} km`} type="blue" />}
                {tmpl.type === 'days' && tmpl.trigger_days && <Badge label={`${tmpl.trigger_days} dias`} type="amber" />}
                {/* [NEW] Indica de relance se esse template dispara email ou só o alerta dentro do app. */}
                <Badge label={emailOn ? '📧 Email' : '📧 Email off'} type={emailOn ? 'green' : 'gray'} />
              </div>
              <div style={{ fontSize: '13px', color: colors.textSec, marginBottom: '4px' }}>{tmpl.message}</div>
              <button onClick={() => handleEdit(tmpl)} style={{ background: 'none', border: 'none', color: colors.primary, fontSize: '12px', fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: "'DM Sans', sans-serif" }}>Editar</button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <SectionHeader title="Templates de alertas" subtitle="Configure os alertas automáticos enviados aos usuários" />

      <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '14px', padding: '24px', marginBottom: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <span style={{ fontSize: '20px' }}>👋</span>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: colors.text }}>Alerta de boas-vindas</div>
            <div style={{ fontSize: '12px', color: colors.textSec }}>Enviado ao cadastrar o primeiro veículo</div>
          </div>
        </div>
        {welcome.map(renderTemplate)}
        {welcome.length === 0 && <div style={{ fontSize: '13px', color: colors.textTer, padding: '12px 0' }}>Nenhum template de boas-vindas configurado</div>}
      </div>

      <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '14px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <span style={{ fontSize: '20px' }}>🔧</span>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: colors.text }}>Alertas de manutenção</div>
            <div style={{ fontSize: '12px', color: colors.textSec }}>Ativados automaticamente após o primeiro serviço do tipo ser registrado. Verificação e envio de email rodam 1x por dia.</div>
          </div>
        </div>
        {maintenance.map(renderTemplate)}
        {maintenance.length === 0 && <div style={{ fontSize: '13px', color: colors.textTer, padding: '12px 0' }}>Nenhum template de manutenção configurado</div>}
      </div>
    </div>
  );
}

const VIEWS = [
  { key: 'overview', label: 'Visão Geral', icon: '📊' },
  { key: 'users', label: 'Usuários', icon: '👥' },
  { key: 'professionals', label: 'Profissionais', icon: '🔧' },
  { key: 'vehicles', label: 'Veículos', icon: '🚗' },
  { key: 'plateConflicts', label: 'Conflitos de placa', icon: '⚠️' },
  { key: 'services', label: 'Serviços', icon: '📋' },
  { key: 'subscriptions', label: 'Assinaturas', icon: '💳' },
  { key: 'alerts', label: 'Alertas', icon: '🔔' },
  { key: 'settings', label: 'Configurações', icon: '⚙️' },
];

export default function AdminDashboard() {
  const [view, setView] = useState('overview');
  const [stats, setStats] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  const checkAuth = useCallback(() => {
    const token = sessionStorage.getItem('nico_admin_token');
    if (!token) {
      setChecking(false);
      return;
    }

    adminAPI.stats()
      .then(r => {
        setStats(r.stats);
        setAuthorized(true);
      })
      .catch(() => {
        sessionStorage.removeItem('nico_admin_token');
      })
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const handleLogout = () => {
    sessionStorage.removeItem('nico_admin_token');
    setAuthorized(false);
  };

  if (checking) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f9fafb', flexDirection: 'column', gap: '12px' }}>
      <div className="spinner" style={{ width: '32px', height: '32px' }} />
      <span style={{ fontSize: '13px', color: '#6b7280' }}>Verificando acesso...</span>
    </div>
  );

  if (!authorized) {
    return <AdminLogin onSuccess={() => { setChecking(true); checkAuth(); }} />;
  }

  const renderView = () => {
    switch (view) {
      case 'overview': return <OverviewView stats={stats}/>;
      case 'users': return <UsersView/>;
      case 'professionals': return <ProfessionalsView/>;
      case 'vehicles': return <VehiclesView/>;
      case 'plateConflicts': return <PlateConflictsView/>;
      case 'services': return <ServicesView/>;
      case 'subscriptions': return <SubscriptionsView/>;
      case 'alerts': return <AlertTemplatesView/>;
      case 'settings': return <SettingsView/>;
      default: return <OverviewView stats={stats}/>;
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #f9fafb; }
        input:focus { outline: 2px solid #14562e; outline-offset: 0; border-color: #14562e !important; }
      `}</style>

      <div style={styles.layout}>
        <aside style={styles.sidebar}>
          <Logo />
          <nav style={{ flex: 1, padding: '12px 0', overflowY: 'auto' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: colors.textTer, textTransform: 'uppercase', letterSpacing: '0.8px', padding: '8px 20px 4px' }}>Principal</div>
            {VIEWS.slice(0, 7).map(v => (
              <NavItem key={v.key} icon={v.icon} label={v.label} active={view === v.key} onClick={() => setView(v.key)}/>
            ))}
            <div style={{ fontSize: '10px', fontWeight: 700, color: colors.textTer, textTransform: 'uppercase', letterSpacing: '0.8px', padding: '16px 20px 4px' }}>Sistema</div>
            {VIEWS.slice(7).map(v => (
              <NavItem key={v.key} icon={v.icon} label={v.label} active={view === v.key} onClick={() => setView(v.key)}/>
            ))}
          </nav>

          <div style={{ padding: '16px', borderTop: `1px solid ${colors.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: colors.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>⚙️</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: colors.text }}>Administrador</div>
                <div style={{ fontSize: '11px', color: colors.textTer }}>Backoffice NICO</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={() => navigate('/garagem')} style={{ flex: 1, padding: '7px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textSec, fontSize: '12px', fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                🌐 App
              </button>
              <button onClick={handleLogout} style={{ flex: 1, padding: '7px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: 'transparent', color: colors.red, fontSize: '12px', fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                Sair
              </button>
            </div>
          </div>
        </aside>

        <main style={styles.main}>
          <header style={styles.header}>
            <span style={{ fontSize: '14px', fontWeight: 600, color: colors.text }}>
              {VIEWS.find(v => v.key === view)?.icon} {VIEWS.find(v => v.key === view)?.label}
            </span>
            <div style={{ fontSize: '12px', color: colors.textTer }}>
              usenico.com.br/admin-nico
            </div>
          </header>
          <div style={styles.content}>
            {renderView()}
          </div>
        </main>
      </div>
    </>
  );
}
